import { useMutation } from '@tanstack/react-query';

import { pointTime } from '../../shared/lib/format';
import { Modal } from '../../shared/ui/modal';
import { Note } from '../../shared/ui/note';
import type { TCustomerSecret } from '../../entities/customer';
import { revokeSourceSecret } from '../../entities/source';

const DAY_MS = 86_400_000;

interface IEndOldDialogProps {
  old: TCustomerSecret | null;
  /** Their live secrets here, this one included: "both" only when there are two. */
  live: number;
  sourceId: string;
  clientName: string;
  now: number;
  timeZone?: string;
  onClose: () => void;
  onEnded: () => void;
}

export const EndOldDialog = ({
  old,
  live,
  sourceId,
  clientName,
  now,
  timeZone,
  onClose,
  onEnded,
}: IEndOldDialogProps) => {
  const end = useMutation({
    mutationFn: (secret: TCustomerSecret) => revokeSourceSecret(sourceId, secret.secretId),
    onSuccess: onEnded,
  });

  const matched = old?.lastUsedAt ?? null;
  const stillUsed = matched !== null && now - new Date(matched).getTime() < DAY_MS;

  return (
    <Modal
      open={old !== null}
      onOpenChange={(open) => {
        if (open) return;
        end.reset();
        onClose();
      }}
      title="End the old secret now?"
      cancelLabel={live === 2 ? 'Keep both' : 'Keep it'}
      submit={{
        label: stillUsed ? 'End it anyway' : 'End it now',
        destructive: true,
        onSubmit: () => old && end.mutate(old),
        waiting: end.isPending,
        waitingLabel: 'Ending',
      }}
    >
      <div className="flex flex-col gap-3">
        <p className="text-data text-primary">
          {matched
            ? `${stillUsed ? `${clientName} still signs with it: ` : ''}the old secret last matched at ${pointTime(matched, now, timeZone)}.`
            : 'The old secret has not matched a request here.'}
        </p>
        <Note tone="caution">
          Their next request signed with it is rejected, and no run starts.
        </Note>
        {end.isError && (
          <Note tone="danger">
            Not ended: {end.error.message}{' '}
            {live === 2 ? 'Both secrets still work.' : 'It still works.'}
          </Note>
        )}
      </div>
    </Modal>
  );
};
